import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Select } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import { dateBackendDateTimeToUI, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { sumMinutesToHours } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/useHoursAndMinutes'
import NumberField from 'modules/trainings/components/NumberField'
import { SessionData } from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import { useSessionsForm } from 'modules/trainings/hooks/Sessions/useSessionsForm'
import { useModalities } from 'modules/trainings/hooks/useModalities'

interface SelfPacedEventBoxProps {
  data: SessionData
  setCurrentData: (value: SessionData) => void
  groupEnd: string
  groupStart: string
}

// edits the 'startsAt', 'endsAt', 'dueDate' and 'duration' fields of the session.
export const SelfPacedEvent = ({
  data,
  setCurrentData,
  groupStart,
  groupEnd,
}: SelfPacedEventBoxProps) => {
  const { buildTime, setHourFromBackend } = useSessionsForm()
  const { modalityOptions } = useModalities()

  const dueDateError =
    data?.dueDate && data?.startsAt && moment(data.dueDate).isBefore(moment(data.startsAt), 'day')
      ? i18n.t(
          'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.due_date_error'
        )
      : undefined

  return (
    <>
      <Box flexDirection="row" gap="s16">
        <DayPicker
          value={data.startsAt ? dateBackendDateTimeToUI(String(data.startsAt)) : ''}
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.start_date'
          )}
          onChange={(date: string) => {
            const startsAtHour = data.startsAt ? setHourFromBackend(String(data.startsAt)) : ''
            setCurrentData({
              ...data,
              startsAt: buildTime(date, startsAtHour || '00:00'),
            })
          }}
          maxDate={stringToDate(groupEnd, 'ui')}
          minDate={stringToDate(groupStart, 'ui')}
        />
        <DayPicker
          value={data.endsAt ? dateBackendDateTimeToUI(String(data.endsAt)) : ''}
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.end_date'
          )}
          onChange={(date: string) => {
            const endsAtHour = data.dueDate ? setHourFromBackend(String(data.dueDate)) : ''
            setCurrentData({
              ...data,
              dueDate: buildTime(date, '0'),
              endsAt: buildTime(date, endsAtHour ?? '0'),
            })
          }}
          mandatory={!!data.startDate}
          error={dueDateError}
          maxDate={stringToDate(groupEnd, 'ui')}
          minDate={stringToDate(groupStart, 'ui')}
        />
      </Box>
      <Box flexDirection="row" gap="s16">
        <NumberField
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.hours'
          )}
          step={1}
          min={0}
          maxLength={5}
          maxDecimals={0}
          value={Math.floor(parseFloat(data.duration || '0'))}
          onChange={(hours) => {
            const minutes = Math.round((parseFloat(data.duration || '0') % 1) * 60).toString()

            setCurrentData({
              ...data,
              duration: String(sumMinutesToHours(Number(hours), Number(minutes))),
            })
          }}
        />
        <NumberField
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.minutes'
          )}
          step={1}
          min={0}
          max={59}
          maxDecimals={0}
          value={Math.round((parseFloat(data.duration || '0') % 1) * 60)}
          onChange={(minutes) => {
            const hours = Math.floor(parseFloat(data.duration || '0')).toString()

            setCurrentData({
              ...data,
              duration: String(sumMinutesToHours(Number(hours), Number(minutes))),
            })
          }}
        />
      </Box>
      <Box gap="s8">
        <Select
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.modality.label'
          )}
          options={modalityOptions}
          placeholder={data.modality}
          value={data.modality}
          defaultItem={modalityOptions[0]}
          onChange={(value) => {
            let updatedData = { ...data, modality: value }

            if (value === 'inperson') {
              updatedData = { ...updatedData, link: '' }
            } else if (value === 'online') {
              updatedData = { ...updatedData, location: '' }
            }
            setCurrentData(updatedData)
          }}
        />
      </Box>
    </>
  )
}
